import { useMemo } from 'react';
import {
  formatTransportDropoffAddress,
  formatTransportPickupAddress,
  formatTransportStatuses,
  useGetTransportsQuery,
  useSelectedRequestId,
  type EtlTransportResponse,
  type EtlVehicleDetails,
  type EtlProviderDetails,
  type EtlDispatchCenterDetails,
  type StatusDisplayItem,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import type { StatusPageState } from '../../types';
import {
  getStatusPageState,
  resolveDisplayableStatusTimeline,
  resolveStatusEtaDisplay,
  type ResolvedStatusEta,
} from '../etlStatusUtils';

interface AirAmbulanceEtlStatusResult {
  transport: EtlTransportResponse | undefined;
  selectedRequestId: string | null;
  isLoading: boolean;
  formattedStatuses: StatusDisplayItem[];
  /** Raw newest status row by timestamp, used for ETA and activity checks. */
  latestStatus: StatusDisplayItem | null;
  /** Newest row with a recognized type and display */
  latestDisplayableStatus: StatusDisplayItem | null;
  /** Older statuses below {@link latestDisplayableStatus} in the accordion. */
  remainingStatuses: StatusDisplayItem[];
  latestVehicle: EtlVehicleDetails | null;
  latestProvider: EtlProviderDetails | null;
  latestDispatchCenter: EtlDispatchCenterDetails | null;
  /** The URL that the user can open in their browser to track the status. */
  trackingUrl: string | null;
  /** Brand tail (aircraft identifier) if available */
  brandTail: string | null;
  /** Latest ETA from the newest status update, when still useful. */
  latestEta: ResolvedStatusEta | null;
  /** Pickup address from the transport request */
  pickupAddress: string | null;
  /** Destination (dropoff) display string from the transport request */
  destination: string | null;
  statusPageState: StatusPageState;
}

/**
 * Hook for Air Ambulance transport status to read from the etl service cache.
 */
export function useAirAmbulanceEtlStatus(): AirAmbulanceEtlStatusResult {
  // AFAIK we only need have a component to show the status of the currently
  // selected transport request. If we need to show status for a non-selected
  // request, we can modify this hook to take a requestId as a parameter.
  const selectedRequestId = useSelectedRequestId();

  const { data, isFetching } = useGetTransportsQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const transports = data?.transports;

  const transport = transports
    ? transports.find(t => t.gmr_transport_id === selectedRequestId)
    : undefined;

  const formattedStatuses = useMemo(
    () => (transport ? formatTransportStatuses(transport.statuses) : []),
    [transport],
  );

  const latestStatus =
    formattedStatuses.length > 0 ? formattedStatuses[0] : null;
  const { latestDisplayableStatus, remainingStatuses } =
    resolveDisplayableStatusTimeline(formattedStatuses);

  const latestVehicle =
    formattedStatuses.find(s => s.vehicle?.name || s.vehicle?.id)?.vehicle ??
    null;
  const latestProvider =
    formattedStatuses.find(s => s.provider?.name)?.provider ?? null;
  const latestDispatchCenter =
    formattedStatuses.find(s => s.dispatchCenter?.name)?.dispatchCenter ?? null;
  const trackingUrl =
    formattedStatuses.find(s => s.trackingUrl)?.trackingUrl ?? null;
  const brandTail = latestVehicle?.name ?? null;
  const latestEta = resolveStatusEtaDisplay(latestStatus, formattedStatuses);
  const statusPageState = getStatusPageState(transport, formattedStatuses);

  const [pickupAddress, destination] = useMemo(
    () => [
      transport ? formatTransportPickupAddress(transport) : null,
      transport ? formatTransportDropoffAddress(transport) : null,
    ],
    [transport],
  );

  return {
    transport,
    selectedRequestId,
    isLoading: isFetching,
    formattedStatuses,
    latestStatus,
    latestDisplayableStatus,
    remainingStatuses,
    latestVehicle,
    latestProvider,
    latestDispatchCenter,
    trackingUrl,
    brandTail,
    latestEta,
    pickupAddress,
    destination,
    statusPageState,
  };
}
